import * as React from 'react';
import {ChevronDown} from 'lucide-react';
import {type SelectOption} from '../../lib/selector-option';
import {cn} from '../../lib/cn';
import {AirbnbSelect} from '../select';
import {AirbnbInput} from '../input';
import {AirbnbFieldHelperText} from '../field-helper-text';
import {FieldErrorMessage} from '../../field-error-message';

type AirbnbPhoneFieldOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type AirbnbPhoneFieldValue = {
  code: string;
  number: string;
};

type AirbnbPhoneFieldProps = {
  label: string;
  topLabel?: string;
  value?: AirbnbPhoneFieldValue | null;
  onChange: (value: AirbnbPhoneFieldValue) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  options: AirbnbPhoneFieldOption[];
  placeholder?: string;
  disabled?: boolean;
  codeReadOnly?: boolean;
  error?: string;
  invalid?: boolean;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  helper?: React.ReactNode;
  className?: string;
  name?: string;
  codeName?: string;
  numberName?: string;
  mobileTitle?: string;
  codePlaceholder?: string;
  defaultCode?: string;
};

function formatPhoneCodeOptionLabel(option: AirbnbPhoneFieldOption) {
  const label = String(option.label);
  const value = String(option.value);

  return label.includes(value) ? label : `${label} (${value})`;
}

const AirbnbPhoneField = React.forwardRef<HTMLInputElement, AirbnbPhoneFieldProps>(
  (
    {
      label,
      topLabel,
      value,
      onChange,
      onBlur,
      options,
      placeholder = 'Phone number',
      disabled,
      codeReadOnly,
      error,
      invalid,
      loading,
      optional,
      tooltip,
      helper,
      className,
      name,
      codeName,
      numberName,
      mobileTitle,
      codePlaceholder = '+00',
      defaultCode,
    },
    ref,
  ) => {
    const inputId = React.useId();
    const effectiveCode = value?.code || defaultCode || '';

    const codeOptions = React.useMemo<SelectOption[]>(
      () =>
        options.map(option => ({
          value: option.value,
          label: formatPhoneCodeOptionLabel(option),
          disabled: option.disabled,
        })),
      [options],
    );

    const selectedCodeOption = React.useMemo(
      () => codeOptions.find(option => option.value === effectiveCode) ?? null,
      [codeOptions, effectiveCode],
    );

    const combinedValue =
      effectiveCode || value?.number ? `${effectiveCode}${value?.number ?? ''}` : '';
    const hasInvalidState = Boolean(error) || Boolean(invalid);
    const isBlocked = Boolean(disabled) || Boolean(loading);
    const isCodeBlocked = isBlocked || Boolean(codeReadOnly);

    return (
      <div className={cn('w-full max-w-[var(--max-field-width)]', className)}>
        {name && (
          <input type="hidden" name={name} value={combinedValue} disabled={disabled} />
        )}
        {codeName && (
          <input
            type="hidden"
            name={codeName}
            value={effectiveCode}
            disabled={disabled}
          />
        )}
        {numberName && (
          <input
            type="hidden"
            name={numberName}
            value={value?.number ?? ''}
            disabled={disabled}
          />
        )}
        {topLabel && (
          <label
            htmlFor={inputId}
            className="mb-3 block text-[16px] font-medium leading-5 text-[var(--chekin-airbnb-ink)]"
          >
            {topLabel}
          </label>
        )}
        <div className="flex items-stretch">
          <AirbnbSelect
            options={codeOptions}
            value={selectedCodeOption}
            onChange={option =>
              onChange({
                code: String(option.value),
                number: value?.number ?? '',
              })
            }
            label={`${label} country code`}
            placeholder={codePlaceholder}
            disabled={isCodeBlocked}
            loading={loading}
            invalid={invalid}
            className="max-w-none shrink-0 basis-[96px]"
            mobileTitle={mobileTitle ?? 'Select country code'}
            dropdownClassName="right-auto w-[280px]"
            getValueLabel={option => String(option.value)}
            renderTrigger={({
              id,
              open,
              disabled: triggerDisabled,
              loading: triggerLoading,
              listboxId,
              triggerRef,
              onClick,
              onKeyDown,
              valueLabel,
            }) => (
              <button
                id={id}
                ref={triggerRef}
                type="button"
                aria-label={`${label} country code`}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-busy={triggerLoading}
                aria-invalid={hasInvalidState}
                disabled={triggerDisabled}
                onClick={onClick}
                onKeyDown={onKeyDown}
                className={cn(
                  'flex h-full min-h-[60px] w-full items-center justify-center gap-2 rounded-l-[16px] rounded-r-none border border-r-0 px-4 text-[16px] font-medium leading-6 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--chekin-airbnb-ink)] focus-visible:ring-offset-2',
                  hasInvalidState
                    ? 'border-[var(--status-danger)] bg-[#F2F2F2] text-[var(--status-danger)]'
                    : 'border-[var(--chekin-airbnb-gray-muted)] bg-[var(--chekin-airbnb-surface-alt)] text-[var(--chekin-airbnb-ink)]',
                  triggerDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : triggerLoading
                      ? 'cursor-progress'
                      : 'cursor-pointer',
                )}
              >
                <span>{valueLabel ?? codePlaceholder}</span>
                <ChevronDown
                  className={cn('h-5 w-5 transition-transform', open ? 'rotate-180' : '')}
                  strokeWidth={2}
                />
              </button>
            )}
          />
          <AirbnbInput
            id={inputId}
            ref={ref}
            type="tel"
            inputMode="tel"
            label={label}
            helperText={placeholder}
            value={value?.number ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            error={error}
            invalid={invalid}
            loading={loading}
            optional={optional}
            tooltip={tooltip}
            renderErrorMessage={false}
            wrapperClassName="min-w-0 flex-1"
            fieldClassName="min-h-[60px] rounded-l-none rounded-r-[16px] border-l-0"
            contentClassName="h-[40px] py-2"
            inputClassName="text-[16px] leading-7"
            onChange={event =>
              onChange({
                code: effectiveCode,
                number: event.target.value,
              })
            }
            onBlur={onBlur}
          />
        </div>
        {error && <FieldErrorMessage message={error} />}
        {!error && <AirbnbFieldHelperText helper={helper} disabled={disabled} />}
      </div>
    );
  },
);
AirbnbPhoneField.displayName = 'AirbnbPhoneField';

export {AirbnbPhoneField};
export type {AirbnbPhoneFieldOption, AirbnbPhoneFieldProps, AirbnbPhoneFieldValue};
